import { ConfirmModal, useModalContext, useToastsContext } from '@fourthwall/components';
import React, { createContext, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMembersQuery } from '@modules/Dashboard/Memberships/modules/Members/hooks/useMembersQuery';
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery';
import { routing } from '@utils/routing';
import { useBadgesQuery } from '../../../../hooks/useBadgesQuery';
import { useDeleteCustomBadgeMutation } from '../../../../hooks/useDeleteCustomBadgeMutation';
import { useDisableCustomBadgeMutation } from '../../../../hooks/useDisableCustomBadgeMutation';
import { useDisableMemberStreakBadgeMutation } from '../../../../hooks/useDisableMemberStreakBadgeMutation';
import { useEnableCustomBadgeMutation } from '../../../../hooks/useEnableCustomBadgeMutation';
import { useEnableMemberStreakBadgeMutation } from '../../../../hooks/useEnableMemberStreakBadgeMutation';
import { getEmojiFormValue } from '../../../../utils';
import { useBadgeForm } from '../../hooks/useBadgeForm/useBadgeForm';
import { useEditCustomBadgeMutation } from '../../hooks/useEditCustomBadgeMutation';
import { useEditMemberStreakBadgeMutation } from '../../hooks/useEditMemberStreakBadgeMutation';
import { getIconPayload, getRulesPayload } from '../../utils';
export const BadgeContext = createContext(undefined);
export const BadgeProvider = ({ children }) => {
    const { badgeId = '' } = useParams();
    const navigate = useNavigate();
    const modal = useModalContext();
    const toast = useToastsContext();
    const { badgesQuery } = useBadgesQuery();
    const { tiersQuery } = useTiersQuery();
    const { editCustomBadgeMutation } = useEditCustomBadgeMutation();
    const { enableCustomBadgeMutation } = useEnableCustomBadgeMutation();
    const { disableCustomBadgeMutation } = useDisableCustomBadgeMutation();
    const { deleteCustomBadgeMutation } = useDeleteCustomBadgeMutation();
    const { editMemberStreakBadgeMutation } = useEditMemberStreakBadgeMutation();
    const { enableMemberStreakBadgeMutation } = useEnableMemberStreakBadgeMutation();
    const { disableMemberStreakBadgeMutation } = useDisableMemberStreakBadgeMutation();
    const badge = badgesQuery.data?.badges.find((badge) => badge.id === parseInt(badgeId));
    const customMemberStreakBadges = badgesQuery.data?.badges.filter((badge) => badge.type === 'MemberStreakCustom');
    const { membersQuery } = useMembersQuery([
        {
            page: 1,
            limit: 9999,
            'member_ids[]': badge?.manuallyAssignedSupporterIds || [],
        },
    ], {
        cacheTime: Infinity,
        enabled: badge?.manuallyAssignedSupporterIds
            ? badge?.manuallyAssignedSupporterIds.length > 0
            : false,
    });
    const enableBadge = useCallback(() => {
        modal.open(ConfirmModal, {
            title: 'Are you sure you want to enable this badge?',
            confirmLabel: 'Enable',
            onConfirm: () => {
                if (badge?.type === 'Custom') {
                    enableCustomBadgeMutation.mutate([{ id: parseInt(badgeId) }], {
                        onSuccess: badgesQuery.refetch,
                    });
                }
                else {
                    enableMemberStreakBadgeMutation.mutate([], {
                        onSuccess: badgesQuery.refetch,
                    });
                }
            },
        });
    }, [
        badgeId,
        badge?.type,
        badgesQuery,
        enableCustomBadgeMutation,
        enableMemberStreakBadgeMutation,
        modal,
    ]);
    const disableBadge = useCallback(() => {
        modal.open(ConfirmModal, {
            title: 'Are you sure you want to disable this badge?',
            confirmLabel: 'Disable',
            onConfirm: () => {
                if (badge?.type === 'Custom') {
                    disableCustomBadgeMutation.mutate([{ id: parseInt(badgeId) }], {
                        onSuccess: badgesQuery.refetch,
                    });
                }
                else {
                    disableMemberStreakBadgeMutation.mutate([], {
                        onSuccess: badgesQuery.refetch,
                    });
                }
            },
        });
    }, [
        badgeId,
        badge?.type,
        badgesQuery,
        disableCustomBadgeMutation,
        disableMemberStreakBadgeMutation,
        modal,
    ]);
    const deleteBadge = useCallback(() => {
        modal.open(ConfirmModal, {
            title: 'Are you sure you want to delete this badge?',
            confirmLabel: 'Delete',
            onConfirm: () => {
                deleteCustomBadgeMutation.mutate([{ id: parseInt(badgeId) }], {
                    onSuccess: () => navigate(routing.memberships.settings.badges.self),
                });
            },
        });
    }, [badgeId, deleteCustomBadgeMutation, modal, navigate]);
    const { form, availableRules, hasAnyRules, hasAvailableRules, hasEmptyRule, getRuleComponent } = useBadgeForm({
        initialValues: {
            text: badge?.text || '',
            emoji: badge
                ? getEmojiFormValue(badge?.icon)
                : {
                    type: 'Emoji',
                    emojiIcon: '😀',
                },
            borderColor: badge?.borderColor,
            backgroundColor: badge?.backgroundColor || undefined,
            textColor: badge?.textColor,
            canBeEarnedByMembers: badge ? badge.rules.length > 0 : false,
            rules: badge?.rules.map((rule) => {
                if (rule.type === 'Tier') {
                    return { type: 'Tier', tierId: rule.tierId };
                }
                if (rule.type === 'MemberStreak') {
                    return { type: 'MemberStreak', monthsFrom: rule.monthsFrom, monthsTo: rule.monthsTo };
                }
                return { type: rule.type };
            }) || [],
            removeWhenRulesNoLongerMet: badge?.removeWhenRulesNoLongerMet || false,
            giveToSelectedMembers: !!badge?.manuallyAssignedSupporterIds?.length,
            manuallyAssignedSupporters: membersQuery.data ? membersQuery.data.members : [],
            likesGivenMin: badge?.rules
                ?.find((rule) => rule.type === 'LikesGiven')
                // @ts-ignore
                ?.minimum.toString(),
            likesGivenMax: badge?.rules
                ?.find((rule) => rule.type === 'LikesGiven')
                // @ts-ignore
                ?.maximum?.toString(),
            likesReceivedMin: badge?.rules
                ?.find((rule) => rule.type === 'LikesReceived')
                // @ts-ignore
                ?.minimum.toString(),
            likesReceivedMax: badge?.rules
                ?.find((rule) => rule.type === 'LikesReceived')
                // @ts-ignore
                ?.maximum?.toString(),
            commentsWrittenMin: badge?.rules
                ?.find((rule) => rule.type === 'CommentsWritten')
                // @ts-ignore
                ?.minimum.toString(),
            commentsWrittenMax: badge?.rules
                ?.find((rule) => rule.type === 'CommentsWritten')
                // @ts-ignore
                ?.maximum?.toString(),
            memberStreakBadges: customMemberStreakBadges?.map((badge) => {
                return {
                    id: badge.id,
                    emoji: getEmojiFormValue(badge.icon),
                    borderColor: badge.borderColor,
                    backgroundColor: badge.backgroundColor,
                    textColor: badge.textColor,
                    // @ts-ignore
                    monthsFrom: badge.rules.find((rule) => rule.type === 'MemberStreak')?.monthsFrom || 0,
                    // @ts-ignore
                    monthsTo: badge.rules.find((rule) => rule.type === 'MemberStreak')?.monthsTo || 0,
                };
            }) || [],
        },
        onSubmit: (values) => {
            if (badge?.type === 'Custom') {
                editCustomBadgeMutation.mutate([
                    {
                        id: parseInt(badgeId),
                    },
                    {
                        text: values.text,
                        icon: getIconPayload(values.emoji),
                        borderColor: values.borderColor,
                        backgroundColor: values.backgroundColor,
                        textColor: values.textColor,
                        rules: getRulesPayload(values),
                        removeWhenRulesNoLongerMet: !!values.removeWhenRulesNoLongerMet,
                        manuallyAssignedSupporterIds: values.giveToSelectedMembers && values.manuallyAssignedSupporters.length
                            ? values.manuallyAssignedSupporters.map((supporter) => supporter.id)
                            : undefined,
                    },
                ], {
                    onSuccess: () => {
                        badgesQuery.refetch();
                        toast.notify('Badge has been updated successfully! It may take up to 2 minutes to reflect changes', { type: 'success' });
                    },
                });
            }
            else {
                editMemberStreakBadgeMutation.mutate([
                    {
                        badges: [
                            {
                                id: parseInt(badgeId),
                                icon: getIconPayload(values.emoji),
                                borderColor: values.borderColor,
                                backgroundColor: values.backgroundColor,
                                textColor: values.textColor,
                            },
                            ...values.memberStreakBadges.map((memberStreakBadge) => ({
                                id: memberStreakBadge.id,
                                icon: getIconPayload(memberStreakBadge.emoji),
                                borderColor: memberStreakBadge.borderColor,
                                backgroundColor: memberStreakBadge.backgroundColor,
                                textColor: memberStreakBadge.textColor,
                                monthsFrom: memberStreakBadge.monthsFrom || undefined,
                                monthsTo: memberStreakBadge.monthsTo || undefined,
                            })),
                        ],
                    },
                ], {
                    onSuccess: () => {
                        badgesQuery.refetch();
                        toast.notify('Badge has been updated successfully! It may take up to 2 minutes to reflect changes', { type: 'success' });
                    },
                });
            }
        },
    });
    const value = useMemo(() => ({
        badge,
        tiers: tiersQuery.data?.tiers,
        form,
        availableRules,
        hasEmptyRule,
        hasAnyRules,
        hasAvailableRules,
        isFetching: !badgesQuery.data?.badges || !tiersQuery.data,
        isLoading: editCustomBadgeMutation.isLoading ||
            enableCustomBadgeMutation.isLoading ||
            disableCustomBadgeMutation.isLoading ||
            deleteCustomBadgeMutation.isLoading ||
            editMemberStreakBadgeMutation.isLoading,
        enableBadge,
        disableBadge,
        deleteBadge,
        getRuleComponent,
    }), [
        badge,
        tiersQuery.data,
        form,
        availableRules,
        hasEmptyRule,
        hasAnyRules,
        hasAvailableRules,
        badgesQuery.data?.badges,
        editCustomBadgeMutation.isLoading,
        enableCustomBadgeMutation.isLoading,
        disableCustomBadgeMutation.isLoading,
        deleteCustomBadgeMutation.isLoading,
        editMemberStreakBadgeMutation.isLoading,
        enableBadge,
        disableBadge,
        deleteBadge,
        getRuleComponent,
    ]);
    return React.createElement(BadgeContext.Provider, { value: value }, children);
};
